import Accordion from "@/common/components/Accordion";
import ImageWithDisplayName from "@/common/components/ImageWithDisplayName";
import SmallBadge from "@/common/components/SmallBadge";
import { cn } from "@/common/utils/cn";
import { auctionProposalLeadValueByDirectiveEntryKlass } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/constants/auctionProposalLeadValue.constant";
import { MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryProposalData } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/types/updatedSummary.type";
import { MaintenanceForwardingsDirectiveEntryKlass } from "@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.type";
import { MaintenanceForwardingDirectiveEntryExecutionEntryData } from "@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type";
import { format } from "date-fns";
import { CalendarCheck2Icon, CheckIcon, ClockIcon, CrownIcon, WrenchIcon, XIcon } from "lucide-react";
import { Tooltip } from "react-tooltip";
import AuctionProposalDetails from "./AuctionProposal/AuctionProposalDetails";

interface AuctionProposalProps {
  proposal: MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryProposalData;
  directiveEntry?: MaintenanceForwardingDirectiveEntryExecutionEntryData | undefined;
  directiveEntryType?: MaintenanceForwardingsDirectiveEntryKlass;
  canAccept: boolean;
  canDisqualify: boolean;
  onAccept: () => void;
  onDisqualify: () => void;
  forwardingUpdatedAt?: number;
  defaultOpen?: boolean;
}

const AuctionProposal = ({
  proposal,
  directiveEntry,
  directiveEntryType,
  canAccept,
  canDisqualify,
  onAccept,
  onDisqualify,
  forwardingUpdatedAt = 0,
  defaultOpen,
}: AuctionProposalProps) => {
  const isFirstPosition = proposal.position === 1;

  const LeadValue = directiveEntryType
    ? auctionProposalLeadValueByDirectiveEntryKlass[directiveEntryType]
    : undefined;

  const tooltipId = `auction-proposal-${proposal.id}`;

  const canExpand = proposal.id && Number(directiveEntry?.objectives?.length) > 0 && directiveEntry?.diagnostic;

  const head = (
    <div className="px-2.5 py-3 w-full rounded-lg flex items-center justify-between gap-2 min-h-12">
      <div className="flex items-center gap-1.5">
        <div className="flex items-center gap-1">
          {isFirstPosition && (
            <CrownIcon
              size={16}
              strokeWidth={1.5}
              className="text-warning-secondary"
            />
          )}

          <p className={cn('text-sm font-medium leading-none', isFirstPosition && 'text-yellow-400')}>
            {proposal.position}º
          </p>
        </div>

        <ImageWithDisplayName
          placeholderIcon={WrenchIcon}
          displayName={proposal.establishment_fantasy_name}
          displayNameClassName="text-sm"
          imageClassName="size-5"
          containerClassName="gap-1.5"
          iconClassName="size-3.5"
        />
      </div>

      <div className="flex items-center gap-1">
        {LeadValue && (
          <LeadValue proposal={proposal} tooltipId={tooltipId} />
        )}

        {proposal.contents?.deadline_to_complete_in_days != null && (
          <SmallBadge
            data-tooltip-id={tooltipId}
            data-tooltip-content="Prazo em dias"
            LeftIcon={ClockIcon}
            text={`${proposal.contents?.deadline_to_complete_in_days} Dias`}
            className="bg-white-200"
          />
        )}

        {proposal.contents?.start_at && (
          <SmallBadge
            data-tooltip-id={tooltipId}
            data-tooltip-content="Data de início"
            LeftIcon={CalendarCheck2Icon}
            text={format(new Date(proposal.contents?.start_at), 'dd/MM HH:mm')}
            className="bg-white-200"
          />
        )}

        {canAccept && (
          <SmallBadge
            data-tooltip-id={tooltipId}
            data-tooltip-content="Aceitar proposta"
            LeftIcon={CheckIcon}
            className="bg-success hover:bg-success-hover transition-colors cursor-pointer text-white"
            onClick={(event) => {
              event.stopPropagation();
              onAccept();
            }}
          />
        )}

        {canDisqualify && (
          <SmallBadge
            data-tooltip-id={tooltipId}
            data-tooltip-content="Desclassificar proposta"
            LeftIcon={XIcon}
            className="bg-danger hover:bg-[#E03E1AAA] transition-colors cursor-pointer text-white"
            onClick={(event) => {
              event.stopPropagation();
              onDisqualify();
            }}
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      {canExpand ? (
        <Accordion
          className="p-0 rounded-lg"
          triggerClassName="p-0"
          bodyClassName="p-0"
          contentClassName="p-0 pb-1"
          iconStyles="mr-2 -ml-1"
          defaultOpen={defaultOpen}
          head={head}
          body={
            <AuctionProposalDetails
              proposal={proposal}
              directiveEntry={directiveEntry}
              forwardingUpdatedAt={forwardingUpdatedAt}
            />
          }
        />
      ) : (
        <div className="w-full bg-white-100 rounded-lg">
          {head}
        </div>
      )}

      <Tooltip id={tooltipId} className="z-50" />
    </>
  );
}

export default AuctionProposal;
